import type { MemberStructure } from '@/hooks/useInsuranceTypeSchema';

/** One insured person on the Add/Edit Policy form (#332). */
export type MemberRow = {
  /** Client-only key so React keeps row identity across add/remove. */
  uid: string;
  name: string;
  dob: string;
  gender: string;
  relation: string;
  /** Free-text list entered as "Diabetes, Asthma". */
  ped: string;
  height_cm: string;
  weight_kg: string;
};

export const MEMBER_RELATIONS: { value: string; label: string }[] = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'father', label: 'Father' },
  { value: 'mother', label: 'Mother' },
  { value: 'father_in_law', label: 'Father-in-Law' },
  { value: 'mother_in_law', label: 'Mother-in-Law' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other' },
];

export const MEMBER_GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

let counter = 0;
const nextUid = () => `m${Date.now().toString(36)}${(counter += 1)}`;

export function emptyMember(relation = ''): MemberRow {
  return { uid: nextUid(), name: '', dob: '', gender: '', relation, ped: '', height_cm: '', weight_kg: '' };
}

/** Rows a fresh form starts with for a structure (individual: one fixed "self"). */
export function initialMembers(structure: MemberStructure | null | undefined): MemberRow[] {
  if (!structure) return [];
  if (structure.mode === 'individual') return [emptyMember('self')];
  if (structure.mode === 'floater') return [emptyMember('self'), emptyMember('')];
  return [];
}

/** Saved `members` from the API as form rows. */
export function membersFromPolicy(raw: unknown): MemberRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((m: Record<string, unknown>) => ({
    uid: nextUid(),
    name: String(m.name ?? ''),
    dob: String(m.dob ?? ''),
    gender: String(m.gender ?? ''),
    relation: String(m.relation ?? ''),
    ped: Array.isArray(m.pre_existing_diseases) ? (m.pre_existing_diseases as string[]).join(', ') : '',
    height_cm: m.height_cm == null ? '' : String(m.height_cm),
    weight_kg: m.weight_kg == null ? '' : String(m.weight_kg),
  }));
}

/** Rows -> API payload. Individual mode always sends relation "self". */
export function membersToPayload(rows: MemberRow[], structure: MemberStructure) {
  return rows.map((r) => {
    const out: Record<string, unknown> = {
      name: r.name.trim(),
      dob: r.dob,
      gender: r.gender,
      relation: structure.mode === 'individual' ? 'self' : r.relation || undefined,
      pre_existing_diseases: r.ped
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
    if (r.height_cm.trim()) out.height_cm = parseInt(r.height_cm, 10);
    if (r.weight_kg.trim()) out.weight_kg = parseInt(r.weight_kg, 10);
    return out;
  });
}

/**
 * Client-side mirror of apps/policies/structure.py. The server is the authority;
 * this only saves a round trip. Keys match the server's field-keyed errors
 * (`members`, `members[2].dob`).
 */
export function validateMembers(
  rows: MemberRow[],
  structure: MemberStructure,
  today: string = new Date().toISOString().slice(0, 10)
): Record<string, string> {
  const errors: Record<string, string> = {};
  const n = rows.length;
  if (structure.mode === 'individual' && n !== 1) {
    errors.members = 'An individual policy covers exactly one insured person.';
  } else if (structure.mode === 'floater') {
    if (n < structure.min) errors.members = `A family floater needs at least ${structure.min} insured members.`;
    else if (structure.max != null && n > structure.max)
      errors.members = `A family floater allows at most ${structure.max} insured members.`;
    else if (rows.filter((r) => r.relation === 'self').length !== 1)
      errors.members = 'A family floater needs exactly one member with relation "Self".';
  }
  rows.forEach((r, i) => {
    if (!r.name.trim()) errors[`members[${i}].name`] = 'Name is required.';
    if (!r.dob) errors[`members[${i}].dob`] = 'Date of birth is required.';
    else if (r.dob > today) errors[`members[${i}].dob`] = 'Date of birth cannot be in the future.';
    if (!r.gender) errors[`members[${i}].gender`] = 'Gender is required.';
    if (structure.relation_required && !r.relation) errors[`members[${i}].relation`] = 'Relation is required.';
  });
  return errors;
}
